import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { Alert, Box, CircularProgress, Drawer, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { STATUS_OPTIONS } from '../utils/statusUtils'
import { supabase } from '../utils/supabase'
import { L } from './ui/LavDropdown'
import { COLOR_OPTIONS } from './ServiceEditorPanel'
import AddAppointmentDialog from './AddAppointmentDialog'
import AppointmentDetailsDialog from './AppointmentDetailsDialog'
import {
	BK, BkIc, BkAvatar, BkPill, BkGroomer, BkEmpty, BkRowMenu,
	bkPetLineSx, bkBreedSx, bkSublineSx, bkIdColSx, bkSvcNameSx,
	bkGhostSx, bkIconBtnSx, bkDangerBtnSx, bkFilterChipSx, bkDayCardSx, bkDayHeadSx,
	bkMoney, bkApptPrice, bkFullName, bkHit,
} from './bookings/bkBits'
import { usePetAvatars } from './bookings/usePetAvatars'
import { getServicePrice } from '../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

const ITEMS_PER_PAGE = 12

// Status → design pill tone (.bk-pill.completed / .noshow / .canceled)
const STATUS_TONE = {
	booked: 'info',
	bookPending: 'warn',
	completed: 'ok',
	noShow: 'warn',
	cancelled: 'muted',
}

// Past outcome chips (.bk-fchips), mapped onto the app's status values.
const PAST_FILTERS = [
	{ id: 'all', label: 'All', status: '' },
	{ id: 'completed', label: 'Completed', status: 'completed' },
	{ id: 'noshow', label: 'No-shows', status: 'noShow' },
	{ id: 'canceled', label: 'Canceled', status: 'cancelled' },
]

/**
 * FutureAppointments — the Upcoming and Past tabs of the Bookings workspace,
 * styled per the Claude Design "Bookings · desktop" spec: day-grouped `.bk-day`
 * cards of `.bk-appt` rows for Upcoming, and the flat `.bk-past` list with the
 * outcome filter chips (.bk-fchips) for Past.
 *
 * `mode` replaces the old "Show Future Appointments" switch. `query` comes from
 * the workspace header's search box and filters the loaded rows client-side.
 */
function FutureAppointments({ merchant, onAppointmentClick, onMessageClient, mode = 'future', query = '' }) {
	const showFutureAppointments = mode !== 'past'
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Appointments state
	const [appointments, setAppointments] = useState([])
	const [loadingAppointments, setLoadingAppointments] = useState(false)
	const [hasMore, setHasMore] = useState(true)
	const [errorMessage, setErrorMessage] = useState('')
	const offsetRef = useRef(0)

	// Past-only outcome filter (server-side). Upcoming has no filters — the
	// header search does the narrowing there, per the design.
	const [selectedStatus, setSelectedStatus] = useState('')

	// Row actions (desktop): inline cancel / no-show confirm panels + the
	// rebook / reschedule slide-over. `rebook` holds the row plus the prefill
	// bundle the Add Appointment flow expects (ids resolved to full rows).
	// cancelingId and noshowId are mutually exclusive — opening one panel
	// closes the other.
	const [cancelingId, setCancelingId] = useState(null)
	const [noshowId, setNoshowId] = useState(null)
	const [actionBusy, setActionBusy] = useState(false)
	const [rebook, setRebook] = useState(null)
	// "Reschedule…" moves the existing visit: it opens the appointment panel
	// straight into the reschedule slot picker (rows are updated in place —
	// never cancelled and rebooked). `rescheduling` holds the chosen row.
	const [rescheduling, setRescheduling] = useState(null)

	const loadAppointments = useCallback(async (reset = false) => {
		if (!merchant?.id) return

		setLoadingAppointments(true)
		setErrorMessage('')

		try {
			const currentOffset = reset ? 0 : offsetRef.current
			const now = dayjs().utc().toISOString()

			// Build query — pet, groomer and price feed the design's row columns.
			// The id columns (user_id / pet_id / employee_id / group_id) power the
			// reschedule & rebook prefill and the cancel actions.
			let query = supabase
				.from('appointments')
				.select(`
					id,
					start_ts,
					end_ts,
					status,
					customer_name,
					phone_number,
					add_ons,
					user_id,
					pet_id,
					employee_id,
					group_id,
					cancelled_at,
					service_price,
					service_types!inner(id, name, duration_minutes, price, appointment_color),
					pets(id, name, breed),
					employees(id, first_name, last_name)
				`)
				.eq('merchant_id', merchant.id)

			// Upcoming is by time (cancelled visits stay visible on their day);
			// History is by ACTION — everything already past, plus any visit that
			// was cancelled, even if its date is still ahead.
			if (showFutureAppointments) {
				query = query.gte('start_ts', now).order('start_ts', { ascending: true })
			} else {
				query = query
					.or(`start_ts.lt.${now},status.eq.cancelled`)
					.order('start_ts', { ascending: false })
			}

			query = query.range(currentOffset, currentOffset + ITEMS_PER_PAGE - 1)

			// Past outcome filter
			if (!showFutureAppointments && selectedStatus) {
				query = query.eq('status', selectedStatus)
			}

			const { data, error } = await query

			if (error) throw error

			const appointmentsWithNames = (data || []).map(appointment => ({
				...appointment,
				customerName: appointment.customer_name || 'Customer'
			}))

			if (reset) {
				setAppointments(appointmentsWithNames)
				offsetRef.current = ITEMS_PER_PAGE
			} else {
				setAppointments(prev => [...prev, ...appointmentsWithNames])
				offsetRef.current = offsetRef.current + ITEMS_PER_PAGE
			}

			// Check if there are more items
			setHasMore(appointmentsWithNames.length === ITEMS_PER_PAGE)

		} catch (e) {
			console.error('Error loading appointments:', e)
			setErrorMessage('Failed to load appointments: ' + e.message)
		} finally {
			setLoadingAppointments(false)
		}
	}, [merchant?.id, showFutureAppointments, selectedStatus])

	// Load appointments when merchant, filter, or mode changes
	useEffect(() => {
		if (merchant?.id) {
			loadAppointments(true) // true = reset
		}
	}, [merchant, selectedStatus, showFutureAppointments, loadAppointments])

	const handleStatusChange = (value) => {
		setSelectedStatus(value)
		offsetRef.current = 0
	}

	const loadMore = useCallback(() => {
		loadAppointments(false)
	}, [loadAppointments])

	// Pet photos for the rows, resolved in one query per page of results.
	const petAvatars = usePetAvatars(useMemo(
		() => appointments.map(a => a.pet_id).filter(Boolean),
		[appointments]
	))

	const tz = merchant?.timezone

	// ── cancellation (design bk-cancelpanel) ──────────────────────────
	// Inside the merchant's late window the panel offers the same explicit
	// choice as AppointmentDetailsDialog: cancel with the policy fee, or
	// without. Outside the window it's a plain no-fee cancel.
	const feeEstimate = useCallback((a) => {
		const type = merchant?.late_cancel_fee_type
		const value = merchant?.late_cancel_fee_value
		if (!type || value == null) return 0
		const svc = getServicePrice(a)
		return type === 'percentage' ? Math.floor(svc * (value / 100)) : value
	}, [merchant?.late_cancel_fee_type, merchant?.late_cancel_fee_value])

	// Display estimate for the no-show fee, mirroring the mark-no-show edge
	// function's cascade: explicit no-show policy first, else the
	// late-cancellation policy. The function stays authoritative — this only
	// tells the merchant what to expect before confirming.
	const noShowFeeEstimate = useCallback((a) => {
		const svc = getServicePrice(a)
		const pick = (type, value) => {
			if (!type || value == null) return null
			return type === 'percentage' ? Math.floor(svc * (value / 100)) : value
		}
		return pick(merchant?.no_show_fee_type, merchant?.no_show_fee_value)
			?? pick(merchant?.late_cancel_fee_type, merchant?.late_cancel_fee_value)
			?? 0
	}, [merchant?.no_show_fee_type, merchant?.no_show_fee_value, merchant?.late_cancel_fee_type, merchant?.late_cancel_fee_value])

	const isLateCancel = useCallback((a) => (
		Boolean(merchant?.late_cancel_hours) &&
		dayjs(a.start_ts).diff(dayjs(), 'hour', true) < merchant.late_cancel_hours
	), [merchant?.late_cancel_hours])

	const askCancel = (a) => {
		setNoshowId(null)
		setCancelingId((cur) => (cur === a.id ? null : a.id))
	}

	const askNoShow = (a) => {
		setCancelingId(null)
		setNoshowId((cur) => (cur === a.id ? null : a.id))
	}

	const confirmCancel = async (a, withFee) => {
		setActionBusy(true)
		setErrorMessage('')
		try {
			if (withFee) {
				// The edge function computes the fee from the merchant's policy and
				// charges the saved payment method (it also covers the whole group).
				const { data, error } = await supabase.functions.invoke('cancel-appointment', {
					body: { appointmentId: a.id },
				})
				if (error) throw error
				if (!data?.success) throw new Error(data?.error || 'Failed to cancel appointment')
			} else {
				// No fee — plain status flip, same path as the details dialog. A
				// grouped (multi-pet) visit cancels as one unit.
				const cancelledAt = new Date().toISOString()
				const patch = { status: 'cancelled', cancelled_at: cancelledAt }
				const { error } = a.group_id
					? await supabase.from('appointments').update(patch).eq('group_id', a.group_id)
					: await supabase.from('appointments').update(patch).eq('id', a.id)
				if (error) throw error
			}
			setCancelingId(null)
			await loadAppointments(true)
		} catch (e) {
			console.error('Error cancelling appointment:', e)
			setErrorMessage(e.message || 'Failed to cancel appointment')
		} finally {
			setActionBusy(false)
		}
	}

	// ── no-show (design bk-cancelpanel variant) ───────────────────────
	// Same path as AppointmentDetailsDialog: the mark-no-show edge function
	// flips the status and charges the configured no-show fee (if any) to the
	// saved payment method — the fee decision lives in the merchant's policy,
	// not in this panel.
	const confirmNoShow = async (a) => {
		setActionBusy(true)
		setErrorMessage('')
		try {
			const { data, error } = await supabase.functions.invoke('mark-no-show', {
				body: { appointmentId: a.id },
			})
			if (error) throw error
			if (!data?.success) throw new Error(data?.error || 'Failed to mark as no-show')
			setNoshowId(null)
			await loadAppointments(true)
		} catch (e) {
			console.error('Error marking no-show:', e)
			setErrorMessage(e.message || 'Failed to mark as no-show')
		} finally {
			setActionBusy(false)
		}
	}

	// ── rebook / reschedule → Add Appointment flow, prefilled except time ──
	const openRebook = async (a) => {
		setActionBusy(true)
		setErrorMessage('')
		try {
			const addIds = (Array.isArray(a.add_ons) ? a.add_ons : [])
				.map((x) => x.service_id)
				.filter(Boolean)
			const [clientRes, addRes] = await Promise.all([
				a.user_id
					? supabase.from('clients').select('*').eq('id', a.user_id).single()
					: Promise.resolve({ data: null }),
				addIds.length
					? supabase.from('service_types').select('id, name, duration_minutes, price').in('id', addIds)
					: Promise.resolve({ data: [] }),
			])
			setRebook({
				appt: a,
				prefill: {
					client: clientRes.data || null,
					// Date defaults to today inside the flow; time is the one thing
					// a rebook must not inherit.
					date: null,
					time: null,
					pet: a.pets?.id ? a.pets : null,
					service: a.service_types || null,
					addOns: addRes.data || [],
					employee: a.employees?.id ? a.employees : null,
				},
			})
		} catch (e) {
			console.error('Error preparing rebook:', e)
			setErrorMessage(e.message || 'Failed to open rebooking')
		} finally {
			setActionBusy(false)
		}
	}

	const closeRebook = () => setRebook(null)

	const handleMessage = (a) => {
		if (onMessageClient && a.user_id) onMessageClient({ id: a.user_id })
	}

	// Header search — client-side over loaded rows (client, pet, service, groomer).
	const filtered = useMemo(() => {
		if (!query.trim()) return appointments
		return appointments.filter((a) => bkHit(
			query, a.customerName, a.pets?.name, a.service_types?.name, bkFullName(a.employees),
		))
	}, [appointments, query])

	// Group into per-day cards. Upcoming groups on both desktop and mobile;
	// Past groups only on mobile (desktop Past is a flat list).
	const grouped = showFutureAppointments || isMobile
	const days = useMemo(() => {
		if (!grouped) return []
		const map = new Map()
		filtered.forEach((a) => {
			const key = dayjs(a.start_ts).tz(tz).format('YYYY-MM-DD')
			if (!map.has(key)) map.set(key, [])
			map.get(key).push(a)
		})
		const today = dayjs().tz(tz).format('YYYY-MM-DD')
		const tomorrow = dayjs().tz(tz).add(1, 'day').format('YYYY-MM-DD')
		return Array.from(map.entries()).map(([date, items]) => {
			const d = dayjs.tz(date, tz)
			const label = showFutureAppointments
				? (date === today
					? `Today · ${d.format('ddd, MMM D')}`
					: date === tomorrow
						? `Tomorrow · ${d.format('ddd, MMM D')}`
						: d.format('dddd, MMM D'))
				: d.format('ddd, MMM D, YYYY')
			// Cancelled visits stay on their day but no longer count toward the
			// day's booked figures.
			const live = items.filter((a) => a.status !== 'cancelled' && a.status !== 'noShow')
			return {
				date,
				label,
				isToday: date === today,
				items,
				liveCount: live.length,
				total: live.reduce((s, a) => s + bkApptPrice(a), 0),
			}
		})
	}, [filtered, grouped, showFutureAppointments, tz])

	// ── one appointment row (.bk-appt / .bk-past) ──────────────────────
	const renderRow = (a, isLast) => {
		const start = dayjs(a.start_ts).tz(tz)
		const end = dayjs(a.end_ts).tz(tz)
		const addons = Array.isArray(a.add_ons) ? a.add_ons : []
		const groomer = bkFullName(a.employees)
		const status = STATUS_OPTIONS.find(s => s.value === a.status)
		const cancelled = a.status === 'cancelled'
		const noshow = a.status === 'noShow'
		// A visit already resolved (cancelled or no-show) keeps its place on the
		// day but reads as an outcome, not a live booking.
		const inactive = cancelled || noshow
		const canceling = cancelingId === a.id
		const noshowing = noshowId === a.id
		const panelOpen = canceling || noshowing
		const late = showFutureAppointments && !inactive && isLateCancel(a)
		const fee = late ? feeEstimate(a) : 0
		const nsFee = noShowFeeEstimate(a)
		const stop = (fn) => (e) => { e.stopPropagation(); fn() }

		// Cancelled context line: when it happened (bk-cxline)
		const cxLine = cancelled && a.cancelled_at ? (
			<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: L.muted, whiteSpace: 'nowrap' }}>
				{`Canceled ${dayjs(a.cancelled_at).tz(tz).format('MMM D, h:mm A')}`}
			</Box>
		) : null

		// Row actions (.bk-appt-actions, design `.on` — always visible).
		// Resolved + history rows: message + Rebook. Live upcoming rows:
		// message + the triple-dot overflow menu.
		const iconActions = inactive || !showFutureAppointments ? (
			<>
				{onMessageClient && a.user_id ? (
					<Box component="button" type="button" title={`Message ${a.customerName}`}
						onClick={stop(() => handleMessage(a))} sx={bkIconBtnSx(false)}>
						<BkIc name="msg" size={14} />
					</Box>
				) : null}
				<Box component="button" type="button" disabled={actionBusy}
					title={`Rebook — opens the appointment form prefilled for ${a.pets?.name || a.customerName}`}
					onClick={stop(() => openRebook(a))} sx={bkGhostSx(true)}>
					<BkIc name="refresh" size={13} sw={1.7} />
					<span>Rebook</span>
				</Box>
			</>
		) : (
			<>
				{onMessageClient && a.user_id ? (
					<Box component="button" type="button" title={`Message ${a.customerName}`}
						onClick={stop(() => handleMessage(a))} sx={bkIconBtnSx(false)}>
						<BkIc name="msg" size={14} />
					</Box>
				) : null}
				<BkRowMenu
					disabled={actionBusy}
					items={[
						{ icon: 'calendar', label: 'Reschedule…', onClick: () => setRescheduling(a) },
						{
							icon: 'warn',
							label: noshowing ? 'Keep appointment' : 'Mark as no-show…',
							onClick: () => askNoShow(a),
						},
						'sep',
						{
							icon: canceling ? 'x' : 'ban',
							label: canceling ? 'Keep appointment' : 'Cancel appointment…',
							danger: true,
							onClick: () => askCancel(a),
						},
					]}
				/>
			</>
		)

		return (
			<Box
				key={a.id}
				onClick={() => onAppointmentClick && onAppointmentClick(a)}
				sx={{
					display: 'grid',
					alignItems: 'center',
					gap: '14px',
					p: '11px 16px',
					borderBottom: isLast ? 'none' : `1px solid ${BK.rowBorder}`,
					cursor: onAppointmentClick ? 'pointer' : 'default',
					// Every row in a mode shares ONE template so the columns line up
					// down the list. Widths are fixed, or `minmax(0, Nfr)` which only
					// divides leftover space — never `auto`, which would size to that
					// row's own content and knock its neighbours out of alignment
					// (cancelled rows carry an extra line the others don't).
					// Upcoming folds status and price into a single band: a live row
					// shows the price there, a cancelled one the pill + context.
					gridTemplateColumns: {
						xs: '1fr',
						md: showFutureAppointments
							? '78px minmax(0, 1.15fr) minmax(0, 1.2fr) 150px 186px 118px'
							: '96px minmax(0, 1.1fr) minmax(0, 1.15fr) 140px 196px 92px 150px',
					},
					bgcolor: panelOpen ? '#FDFBFB' : 'transparent',
					'&:hover': { bgcolor: panelOpen ? '#FDFBFB' : BK.inset },
				}}
			>
				{/* .bk-appt-time (struck when the visit was cancelled) */}
				<Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'baseline', md: 'stretch' }, gap: { xs: '8px', md: '1px' } }}>
					<Box component="strong" sx={{
						fontSize: '13.5px', fontWeight: 750, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
						...(cancelled ? {
							textDecoration: 'line-through', textDecorationColor: '#C9C2DC',
							textDecorationThickness: '1.5px', color: '#6E6886',
						} : {}),
					}}>
						{showFutureAppointments ? start.format('h:mm A') : start.format('MMM D')}
					</Box>
					<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: L.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
						{showFutureAppointments ? end.format('h:mm A') : start.format('h:mm A')}
					</Box>
				</Box>

				{/* .bk-appt-main */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, opacity: inactive ? 0.75 : 1 }}>
					<BkAvatar size={34} src={petAvatars[a.pet_id]} alt={a.pets?.name || ''} />
					<Box sx={bkIdColSx}>
						<Box sx={bkPetLineSx}>
							<strong>{a.pets?.name || a.customerName}</strong>
							{a.pets?.breed ? <Box component="span" sx={bkBreedSx}>{a.pets.breed}</Box> : null}
						</Box>
						<Box component="span" sx={bkSublineSx}>{a.customerName}</Box>
					</Box>
				</Box>

				{/* .bk-appt-svc */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, opacity: inactive ? 0.75 : 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
						<Box sx={{
							width: 8, height: 8, borderRadius: '3px', flex: 'none',
							bgcolor: COLOR_OPTIONS.find(o => o.value === a.service_types?.appointment_color)?.color || L.primary,
						}} />
						<Box component="span" sx={{ ...bkSvcNameSx, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							{a.service_types?.name}
						</Box>
					</Box>
					<Box component="span" sx={{ ...bkSublineSx, pl: '15px' }}>
						{addons.length ? `+ ${addons.map(x => x.name).join(', ')}` : `${a.service_types?.duration_minutes} min`}
					</Box>
				</Box>

				{/* .bk-appt-gr */}
				<Box sx={{ minWidth: 0, opacity: inactive ? 0.75 : 1 }}><BkGroomer name={groomer} mutedLabel="Unassigned" /></Box>

				{/* status / price cell */}
				{!showFutureAppointments || inactive ? (
					// .bk-past-status — pill + one line of context
					<Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
						<BkPill tone={STATUS_TONE[a.status] || 'muted'} label={status?.label || a.status} />
						{cxLine}
					</Box>
				) : null}
				{showFutureAppointments && inactive ? null : (
					<Box sx={{
						fontSize: '13.5px', fontWeight: 750, fontVariantNumeric: 'tabular-nums',
						whiteSpace: 'nowrap', textAlign: { xs: 'left', md: 'right' },
						...(cancelled ? { color: L.muted, fontWeight: 600 } : {}),
					}}>
						{cancelled ? '—' : bkMoney(bkApptPrice(a))}
					</Box>
				)}

				{/* .bk-appt-actions — always visible (revised from the design's
				    hover-reveal, per product decision) */}
				<Box
					onClick={(e) => e.stopPropagation()}
					sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}
				>
					{iconActions}
				</Box>

				{/* .bk-cancelpanel — inline confirm, full row width. Inside the late
				    window the choice is explicit, matching AppointmentDetailsDialog:
				    cancel without the fee, or cancel with it. */}
				{canceling ? (
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							gridColumn: '1 / -1', borderTop: `1px solid ${BK.insetBorder}`,
							mt: '4px', p: '14px 0 4px', cursor: 'default',
							display: 'flex', flexDirection: 'column', gap: '11px',
						}}
					>
						<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em' }}>
							{`Cancel ${a.pets?.name || a.customerName}’s appointment?`}
						</Box>
						<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, lineHeight: 1.45, mt: '-6px' }}>
							{`${start.format('ddd, MMM D')} · ${start.format('h:mm A')}${groomer ? ` with ${groomer}` : ''}. `}
							{`${a.customerName} is notified, the time reopens, and the cancellation stays on record in History.`}
							{late && fee > 0
								? ` This is within your ${merchant.late_cancel_hours}h late-cancellation window — canceling with the fee charges ${bkMoney(fee)} to the saved payment method.`
								: ''}
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '9px', pt: '2px', flexWrap: 'wrap' }}>
							<Box component="button" type="button" disabled={actionBusy}
								onClick={stop(() => askCancel(a))} sx={bkGhostSx(true)}>
								Keep appointment
							</Box>
							{late && fee > 0 ? (
								<>
									<Box component="button" type="button" disabled={actionBusy}
										onClick={stop(() => confirmCancel(a, false))}
										sx={{ ...bkGhostSx(true), color: BK.danger, '&:hover': { borderColor: BK.danger, color: BK.dangerDeep } }}>
										{actionBusy
											? <CircularProgress size={14} sx={{ color: 'inherit' }} />
											: null}
										<span>Cancel without fee</span>
									</Box>
									<Box component="button" type="button" disabled={actionBusy}
										onClick={stop(() => confirmCancel(a, true))} sx={bkDangerBtnSx}>
										{actionBusy
											? <CircularProgress size={14} sx={{ color: 'inherit' }} />
											: <BkIc name="ban" size={13} sw={1.8} />}
										<span>{`Cancel with ${bkMoney(fee)} fee`}</span>
									</Box>
								</>
							) : (
								<Box component="button" type="button" disabled={actionBusy}
									onClick={stop(() => confirmCancel(a, false))} sx={bkDangerBtnSx}>
									{actionBusy
										? <CircularProgress size={14} sx={{ color: 'inherit' }} />
										: <BkIc name="ban" size={13} sw={1.8} />}
									<span>Cancel & notify</span>
								</Box>
							)}
						</Box>
					</Box>
				) : null}

				{/* .bk-cancelpanel variant — mark as no-show. Same path as
				    AppointmentDetailsDialog: the edge function flips the status and
				    charges the policy's no-show fee (if configured). */}
				{noshowing ? (
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							gridColumn: '1 / -1', borderTop: `1px solid ${BK.insetBorder}`,
							mt: '4px', p: '14px 0 4px', cursor: 'default',
							display: 'flex', flexDirection: 'column', gap: '11px',
						}}
					>
						<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em' }}>
							{`Mark ${a.pets?.name || a.customerName} as a no-show?`}
						</Box>
						<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, lineHeight: 1.45, mt: '-6px' }}>
							{`${start.format('ddd, MMM D')} · ${start.format('h:mm A')}${groomer ? ` with ${groomer}` : ''}. `}
							{`The visit settles into History as a no-show`}
							{nsFee > 0
								? `, and your policy charges the ${bkMoney(nsFee)} no-show fee to the saved payment method.`
								: '. Your policy has no no-show fee, so nothing is charged.'}
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '9px', pt: '2px' }}>
							<Box component="button" type="button" disabled={actionBusy}
								onClick={stop(() => askNoShow(a))} sx={bkGhostSx(true)}>
								Keep appointment
							</Box>
							<Box component="button" type="button" disabled={actionBusy}
								onClick={stop(() => confirmNoShow(a))} sx={bkDangerBtnSx}>
								{actionBusy
									? <CircularProgress size={14} sx={{ color: 'inherit' }} />
									: <BkIc name="warn" size={13} sw={1.8} />}
								<span>{nsFee > 0 ? 'Mark no-show & charge' : 'Mark no-show'}</span>
							</Box>
						</Box>
					</Box>
				) : null}
			</Box>
		)
	}

	// ── mobile row (.bkm-appt-row / .bkm-past) — tap opens the appointment ──
	const renderMobileRow = (a, idx) => {
		const start = dayjs(a.start_ts).tz(tz)
		const end = dayjs(a.end_ts).tz(tz)
		const mins = end.diff(start, 'minute')
		const addons = Array.isArray(a.add_ons) ? a.add_ons : []
		const status = STATUS_OPTIONS.find(s => s.value === a.status)
		// Past rows drop the divider on the first row; upcoming keep it under the header.
		const topBorder = !showFutureAppointments && idx === 0 ? 'none' : `1px solid ${BK.rowBorder}`

		return (
			<Box
				key={a.id}
				component="button"
				type="button"
				onClick={() => onAppointmentClick && onAppointmentClick(a)}
				sx={{
					display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left',
					p: '12px 14px 12px 16px', bgcolor: 'transparent', border: 'none', borderTop: topBorder,
					minHeight: 56, cursor: onAppointmentClick ? 'pointer' : 'default', fontFamily: 'inherit',
					'&:active': { bgcolor: BK.inset },
				}}
			>
				{/* .bkm-appt-time */}
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 'none', width: 62 }}>
					<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 750, fontVariantNumeric: 'tabular-nums', color: L.text }}>
						{start.format('h:mm A')}
					</Box>
					<Box component="span" sx={{ fontSize: '11px', fontWeight: 600, color: L.muted }}>
						{mins} min
					</Box>
				</Box>

				{/* .bk-req-id */}
				<Box sx={{ ...bkIdColSx, flex: 1 }}>
					<Box sx={bkPetLineSx}>
						<strong>{a.pets?.name || a.customerName}</strong>
						{a.pets?.breed ? <Box component="span" sx={bkBreedSx}>{a.pets.breed}</Box> : null}
					</Box>
					<Box component="span" sx={bkSublineSx}>
						{a.service_types?.name}{addons.length ? ` +${addons.length}` : ''} · {a.customerName}
					</Box>
				</Box>

				{/* .bkm-appt-right */}
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flex: 'none' }}>
					<Box component="span" sx={{ fontSize: '13.5px', fontWeight: 750, fontVariantNumeric: 'tabular-nums', color: L.text }}>
						{bkMoney(bkApptPrice(a))}
					</Box>
					{!showFutureAppointments ? (
						<BkPill tone={STATUS_TONE[a.status] || 'muted'} label={status?.label || a.status} />
					) : null}
				</Box>
			</Box>
		)
	}

	const isEmpty = !loadingAppointments && filtered.length === 0
	const isSearching = Boolean(query.trim())

	return (
		<Box sx={{ fontFamily: L.font, color: L.text }}>
			{/* ── Past outcome filter chips (.bk-fchips / .bkm-fchips) ── */}
			{!showFutureAppointments && (
				<Box sx={{
					display: 'flex', gap: '8px', mb: '14px',
					...(isMobile
						? {
							flexWrap: 'nowrap', overflowX: 'auto', pb: '2px',
							scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
						}
						: { flexWrap: 'wrap' }),
				}}>
					{PAST_FILTERS.map((f) => (
						<Box
							key={f.id} component="button" type="button"
							onClick={() => handleStatusChange(f.status)}
							sx={{ ...bkFilterChipSx(selectedStatus === f.status), ...(isMobile ? { flex: 'none' } : {}) }}
						>
							{f.label}
						</Box>
					))}
				</Box>
			)}

			{errorMessage ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{errorMessage}</Alert> : null}

			{/* ── list ── */}
			{loadingAppointments && appointments.length === 0 ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', py: '44px', color: L.muted, fontSize: '13px', fontWeight: 650 }}>
					<CircularProgress size={18} sx={{ color: L.primary }} />
					<span>Loading appointments…</span>
				</Box>
			) : isEmpty ? (
				<BkEmpty
					icon={showFutureAppointments ? 'calendar' : 'receipt'}
					title={
						isSearching
							? `No ${showFutureAppointments ? 'upcoming visits' : 'past visits'} match “${query.trim()}”`
							: showFutureAppointments ? 'Nothing on the books yet' : 'No past visits yet'
					}
					sub={
						isSearching
							? 'Try a different name, pet or service.'
							: showFutureAppointments
								? 'Approved requests and calendar bookings show up here.'
								: 'Completed, canceled and no-show visits will show up here.'
					}
				/>
			) : isMobile ? (
				// .bkm-day cards — day-grouped, mobile rows (both upcoming and past)
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{days.map((d) => (
						<Box key={d.date} sx={{
							bgcolor: BK.card, border: `1px solid ${L.border}`, borderRadius: '16px',
							boxShadow: BK.cardShadow, overflow: 'hidden',
						}}>
							<Box sx={{
								display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
								gap: '8px', p: '13px 16px 10px',
							}}>
								<Box component="strong" sx={{
									fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em',
									color: d.isToday ? L.primaryDeep : L.text,
								}}>
									{d.label}
								</Box>
								{showFutureAppointments ? (
									<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: L.muted }}>
										{d.items.length} · {bkMoney(d.total)}
									</Box>
								) : null}
							</Box>
							{d.items.map((a, i) => renderMobileRow(a, i))}
						</Box>
					))}
				</Box>
			) : showFutureAppointments ? (
				// .bk-day cards
				days.map((d) => (
					<Box key={d.date} sx={bkDayCardSx}>
						<Box sx={bkDayHeadSx}>
							<Box component="strong" sx={{
								fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em',
								color: d.isToday ? L.primaryDeep : L.text,
							}}>
								{d.label}
							</Box>
							<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: L.muted }}>
								{d.liveCount} appointment{d.liveCount === 1 ? '' : 's'} · {bkMoney(d.total)} booked
							</Box>
						</Box>
						{d.items.map((a, i) => renderRow(a, i === d.items.length - 1))}
					</Box>
				))
			) : (
				// .bk-wlist — flat past list
				<Box sx={{ bgcolor: BK.card, border: `1px solid ${L.border}`, borderRadius: '14px', boxShadow: BK.cardShadow, overflow: 'hidden' }}>
					{filtered.map((a, i) => renderRow(a, i === filtered.length - 1))}
				</Box>
			)}

			{/* Load More — hidden while searching, since search only covers loaded rows */}
			{hasMore && appointments.length > 0 && !isSearching && (
				<Box sx={{ display: 'flex', justifyContent: 'center', pt: '14px' }}>
					<Box
						component="button" type="button" onClick={loadMore} disabled={loadingAppointments}
						sx={bkGhostSx(false)}
					>
						{loadingAppointments ? <CircularProgress size={15} sx={{ color: 'inherit' }} /> : null}
						<span>{loadingAppointments ? 'Loading…' : 'Load more'}</span>
					</Box>
				</Box>
			)}

			{/* Rebook / reschedule — the Add Appointment flow in a right slide-over
			    (design bkrb-drawer), prefilled with everything except the time. */}
			{!isMobile && rebook && (
				<Drawer
					anchor="right"
					open
					onClose={closeRebook}
					PaperProps={{
						sx: {
							width: 430,
							maxWidth: '100%',
							bgcolor: '#FDFCFF',
							borderLeft: `1px solid ${L.border}`,
							boxShadow: '-22px 0 60px rgba(46, 42, 64, 0.20)',
							backgroundImage: 'none',
						},
					}}
					slotProps={{
						backdrop: {
							sx: { bgcolor: 'rgba(46, 42, 64, 0.30)', backdropFilter: 'blur(1.5px)' },
						},
					}}
				>
					<AddAppointmentDialog
						open
						onClose={closeRebook}
						merchant={merchant}
						onAppointmentCreated={() => {
							closeRebook()
							loadAppointments(true)
						}}
						prefilledAppointment={rebook.prefill}
						desktopVariant="drawer"
					/>
				</Drawer>
			)}

			{/* Reschedule — the appointment panel opened in reschedule mode for the
			    chosen row (desktop slide-over / mobile takeover, per the panel). */}
			<AppointmentDetailsDialog
				open={Boolean(rescheduling)}
				onClose={() => setRescheduling(null)}
				appointment={rescheduling}
				merchant={merchant}
				initialReschedule
				onAppointmentUpdated={() => loadAppointments(true)}
			/>
		</Box>
	)
}

export default FutureAppointments
